
    // IMPORTS
    import { computed, defineComponent, provide, ref, Ref } from 'vue'
    import { useUserStore } from '@/store/user'
    import { useContactSupportStore } from '@/store/contactSupportStore'
    import { AccountApi, buildLocalization, StringUtil, focusFirstInvalidField } from '@/util'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import InputField from '@/components/shopping-cart/input-field.vue'
    import InputDropdown from '@/components/common/input-dropdown.vue'
    import LinkButton from '@/components/common/link-button.vue'
    import PhoneField from '@/components/common/phone-field.vue'

    const CATEGORY_JSP = 'supportCategoryJsp'
    const CATEGORY_EQUIPMENT = 'supportCategoryEquipment'
    const MAX_MESSAGE_LENGTH = 5000

    export default defineComponent({
        name: 'ContactSupport',

        components: {
            LoaderGuard,
            MyIcon,
            InputField,
            InputDropdown,
            LinkButton,
            PhoneField,
            Card,
            CardHeader,
            CardTitle,
            CardBody
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const userStore = useUserStore()
            const formStore = useContactSupportStore()
            const { localize } = buildLocalization()

            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const isSubmitting = ref(false)
            const submitSuccess = ref(false)
            const isValidationActive = ref(false)
            const currentValidationStatus: Ref<{ [id: string]: boolean }> = ref({})

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const hasLoaded = computed(() => userStore.userData != null)
            const email = computed(() => userStore.userData?.currentUser ?? '')
            const currentUser = computed(() => userStore.userData?.partnerAccount.userAccounts.find(u => u.email === email.value))
            const fullName = computed(() => currentUser.value == null
                ? ''
                : `${currentUser.value.firstName} ${currentUser.value.lastName}`
            )
            const supportEmail = computed(() => userStore.userData?.supportEmail ?? '')
            const isEquipmentCategory = computed(() => formStore.category === CATEGORY_EQUIPMENT)
            const categoryItems = computed(() => [
                { value: CATEGORY_JSP, text: localize(CATEGORY_JSP) },
                { value: CATEGORY_EQUIPMENT, text: localize(CATEGORY_EQUIPMENT) }
            ])
            const anyValidationError = computed(
                () => Object.values(currentValidationStatus.value).find(v => v === false) === false
            )

            /********************************************************************************************************************
             PROVIDE
             ********************************************************************************************************************/
            provide('isValidationActive', isValidationActive)
            provide('setValidationResult', (id: string, isValid: boolean) => {
                currentValidationStatus.value[id] = isValid
            })

            /********************************************************************************************************************
             METHODS
             ********************************************************************************************************************/
            function validateMessage(value: string): string | null {
                if (value.length > MAX_MESSAGE_LENGTH) {
                    return localize('maxAllowedLength').replace('{0}', MAX_MESSAGE_LENGTH.toString())
                }
                return null
            }

            function scrollToFirstError() {
                const fieldOrder = [
                    'category',
                    ...(isEquipmentCategory.value ? ['equipmentSerialNo'] : []),
                    'subject',
                    'message'
                ]
                focusFirstInvalidField(fieldOrder, (id) => currentValidationStatus.value[id] === false)
            }

            /********************************************************************************************************************
             EVENT HANDLERS
             ********************************************************************************************************************/
            function onSubmitAnother() {
                isValidationActive.value = false
                currentValidationStatus.value = {}
                submitSuccess.value = false
            }

            function onSubmit() {
                isValidationActive.value = false
                isValidationActive.value = true

                if (anyValidationError.value) {
                    scrollToFirstError()
                    return
                }

                isSubmitting.value = true

                const api = new AccountApi()
                api.contactSupport({
                    name: fullName.value,
                    email: email.value,
                    phoneNumber: StringUtil.hasContent(formStore.phoneNumber) ? formStore.phoneNumber : null,
                    category: formStore.category,
                    equipmentSerialNo: isEquipmentCategory.value ? formStore.equipmentSerialNo : null,
                    subject: formStore.subject,
                    message: formStore.message
                })
                    .then(() => {
                        submitSuccess.value = true
                        formStore.reset()
                    })
                    .finally(() => {
                        isSubmitting.value = false
                    })
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            const publicData = { formStore, isSubmitting, submitSuccess }
            const computedData = { hasLoaded, fullName, email, supportEmail, isEquipmentCategory, categoryItems }
            const publicMethods = { localize, validateMessage, onSubmitAnother, onSubmit }

            return { ...publicData, ...computedData, ...publicMethods }
        }
    })
